import { useMenuNavigation } from "./hooks/useMenuNavigation.js";
import { ScreenStateContext } from "./hooks/useScreenState.js";
import { Fragment, useEffect, useState } from "react";
import { Alert, Button, Spin } from "antd";
import {
  Archive,
  BookOpen,
  CalendarDays,
  ClipboardList,
  Clock3,
  CreditCard,
  GraduationCap,
  Settings,
} from "lucide-react";
import { Shell } from "./components/Shell.jsx";
import { useAcademyStore } from "./hooks/useAcademyStore.js";
import { AcademyManagementPage } from "./features/academy/AcademyManagementPage.jsx";
import { StudentsPage } from "./features/students/StudentsPage.jsx";
import { TimetablePage } from "./features/timetable/TimetablePage.jsx";
import { AcademyCalendarPage } from "./features/calendar/AcademyCalendarPage.jsx";
import { ArchivePage } from "./features/archive/ArchivePage.jsx";
import { ExamPeriodsPage } from "./features/examPeriods/ExamPeriodsPage.jsx";
import { PaymentsPage } from "./features/payments/PaymentsPage.jsx";
import { PointManagementPage } from "./features/payments/PaymentPlaceholderPage.jsx";
import { AuthGate } from "./features/auth/AuthGate.jsx";
import { isManager } from "./lib/supabase.js";
const NAV_ITEMS = [
  { id: "calendar", label: "학원 일정", icon: CalendarDays },
  { id: "students", label: "학생 관리", icon: GraduationCap },
  {
    id: "payments",
    label: "납부 관리",
    icon: CreditCard,
    children: [
      { id: "payments-management", label: "납부 관리" },
      { id: "payments-points", label: "포인트 관리" },
    ],
  },
  { id: "courses", label: "수업관리", icon: BookOpen },
  { id: "timetable", label: "시간표", icon: Clock3 },
  { id: "examPeriods", label: "시험 일정", icon: ClipboardList },
  { id: "archive", label: "기출 보관함", icon: Archive },
  { id: "academy", label: "학원 관리", icon: Settings },
];
export function App() {
  return (
    <AuthGate>
      {(context, logout) => (
        <Workspace key={`${context.userId}:${context.academyId}:${context.role}`} context={context} logout={logout} />
      )}
    </AuthGate>
  );
}
function Workspace({ context, logout }) {
  const store = useAcademyStore(context);
  const [activeView, handleNavigate, navigation] = useMenuNavigation(store.isExamEditing, context);
  const [pageTitleOverride, setPageTitleOverride] = useState("");
  const navItems =
    context.role === "kiosk"
      ? []
      : NAV_ITEMS.filter(
          (item) => item.id !== "payments" || isManager(context.role),
        );
  const permitted = navItems.some(
    (item) =>
      item.id === activeView ||
      item.children?.some((child) => child.id === activeView),
  );
  useEffect(() => {
    if (activeView !== "students" || navigation.route.kind !== "student") {
      setPageTitleOverride("");
      store.setSelectedStudentId("");
    }
  }, [activeView, navigation.route.kind, store.setSelectedStudentId]);
  const props = { store, navigation, setActiveView: handleNavigate, setPageTitleOverride };
  const page = !permitted ? (
    <Alert
      className={activeView === "examPeriods" ? "exam-page-alert" : undefined}
      type="info"
      message={
        context.role === "kiosk"
          ? "키오스크 계정은 출결 전용 화면에서 이용해 주세요."
          : "이 화면에 대한 접근 권한이 없습니다."
      }
    />
  ) : (
    {
      calendar: <AcademyCalendarPage {...props} />,
      students: <StudentsPage {...props} />,
      "payments-management": <PaymentsPage store={store} navigation={navigation} />,
      "payments-points": <PointManagementPage />,
      courses: (
        <AcademyManagementPage key="courses" {...props} mode="courses" />
      ),
      timetable: <TimetablePage {...props} />,
      archive: <ArchivePage {...props} />,
      examPeriods: <ExamPeriodsPage {...props} />,
      academy: <AcademyManagementPage key="academy" {...props} />,
    }[activeView]
  );
  return (
    <ScreenStateContext.Provider value={navigation.screenState}>
    <Shell
      activeView={activeView}
      navItems={navItems}
      onNavigate={handleNavigate}
      onReset={store.refresh}
      onClear={logout}
      pageTitleOverride={pageTitleOverride}
      busy={store.loading || store.saving}
      userLabel={`${context.profile?.display_name || "키오스크"} · ${context.role}`}
    >
      {store.error && (
        <Alert
          className={activeView === "examPeriods" ? "exam-page-alert" : undefined}
          type="error"
          showIcon
          message={store.error}
          action={
            <Button onClick={store.refresh} disabled={store.saving}>
              재시도
            </Button>
          }
        />
      )}
      <Spin
        spinning={store.loading || store.saving}
        tip={store.saving ? "저장하고 있습니다." : "불러오고 있습니다."}
      >
        <fieldset
          disabled={store.loading || store.saving}
          className="workspace-fieldset"
        >
          <Fragment key={JSON.stringify(navigation.route)}>{page}</Fragment>
        </fieldset>
      </Spin>
    </Shell>
    </ScreenStateContext.Provider>
  );
}
